import { useCallback, useEffect, useState, type FormEvent } from 'react'
import type { Combatant, Encounter, Role } from '@shared/types'
import { CONDITIONS } from '@shared/rules/constants'
import { ApiError, api } from '@/lib/api'
import {
  Badge, Button, EmptyState, ErrorNote, Field, Input, Modal, NumberField, Panel, SectionHeading,
} from '@/components/ui'
import { cn } from '@/lib/cn'

const POLL_MS = 3000

const STATUS_LABEL: Record<Combatant['status'], string> = {
  healthy: 'Unhurt', hurt: 'Hurt', bloodied: 'Bloodied', down: 'Down',
}

const STATUS_TONE: Record<Combatant['status'], string> = {
  healthy: 'text-moss-500', hurt: 'text-gold-400', bloodied: 'text-blood-500', down: 'text-ink-500',
}

export function InitiativeTab({ campaignId, myRole }: { campaignId: string; myRole: Role }) {
  const [encounter, setEncounter] = useState<Encounter | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [adding, setAdding] = useState(false)
  const isDm = myRole === 'dm'

  const refresh = useCallback(async () => {
    try {
      const res = await api.getEncounter(campaignId)
      setEncounter(res.encounter)
    } catch {
      // Transient; the next poll picks it up.
    } finally {
      setLoading(false)
    }
  }, [campaignId])

  // Players need to see whose turn it is without refreshing, so this polls on
  // the same rationale as the roll feed: no socket, no Durable Object.
  useEffect(() => {
    void refresh()
    let timer: ReturnType<typeof setInterval> | null = null
    const start = () => { if (!timer) timer = setInterval(() => void refresh(), POLL_MS) }
    const stop = () => { if (timer) { clearInterval(timer); timer = null } }
    const onVisibility = () => { if (document.hidden) stop(); else { void refresh(); start() } }

    document.addEventListener('visibilitychange', onVisibility)
    if (!document.hidden) start()
    return () => { document.removeEventListener('visibilitychange', onVisibility); stop() }
  }, [refresh])

  async function act(action: () => Promise<{ encounter: Encounter } | { ok: true }>) {
    setError(null)
    try {
      const res = await action()
      if ('encounter' in res) setEncounter(res.encounter)
      else await refresh()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work')
    }
  }

  if (loading) return <Panel className="p-6 text-center text-sm text-ink-500">Loading…</Panel>

  if (!encounter) {
    return (
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <EmptyState
          title="No combat running"
          description={isDm
            ? 'Start one and the whole party gets added automatically with their hit points and AC. Everyone can see whose turn it is.'
            : 'Your DM has not started a combat yet.'}
          action={isDm ? <Button variant="primary" onClick={() => setStarting(true)}>Start combat</Button> : undefined}
        />
        <StartModal
          open={starting}
          onClose={() => setStarting(false)}
          onStart={(input) => act(() => api.startEncounter(campaignId, input)).then(() => setStarting(false))}
        />
      </div>
    )
  }

  const current = encounter.turnIndex >= 0 ? encounter.combatants[encounter.turnIndex] : null

  return (
    <div className="space-y-5">
      <SectionHeading
        action={
          isDm && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => act(() => api.rollInitiative(campaignId, true))}>
                Reroll all
              </Button>
              <Button size="sm" onClick={() => setAdding(true)}>Add creature</Button>
              <Button size="sm" variant="ghost" onClick={() => act(() => api.endEncounter(campaignId))}>
                End combat
              </Button>
            </div>
          )
        }
      >
        {encounter.name}
      </SectionHeading>

      {error && <ErrorNote>{error}</ErrorNote>}

      <Panel className="flex flex-wrap items-center justify-between gap-4 p-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-ink-500">Round</p>
          <p className="font-mono text-3xl leading-none tabular-nums text-gold-400">{encounter.round}</p>
        </div>
        <div className="min-w-0 flex-1 text-center">
          <p className="text-xs uppercase tracking-wider text-ink-500">Up now</p>
          <p className="truncate font-display text-xl">{current ? current.name : 'Not started'}</p>
        </div>
        {isDm && (
          <div className="flex gap-2">
            <Button size="sm" onClick={() => act(() => api.advanceTurn(campaignId, true))}
              disabled={encounter.turnIndex < 0}>
              ← Back
            </Button>
            <Button size="sm" variant="primary" onClick={() => act(() => api.advanceTurn(campaignId))}>
              {encounter.turnIndex < 0 ? 'Begin' : 'Next turn'} →
            </Button>
          </div>
        )}
      </Panel>

      {encounter.combatants.length === 0 ? (
        <EmptyState title="Nobody in the fight yet" description="Add a creature to get started." />
      ) : (
        <ol className="space-y-2">
          {encounter.combatants.map((combatant, index) => (
            <CombatantRow
              key={combatant.id}
              combatant={combatant}
              active={index === encounter.turnIndex}
              isDm={isDm}
              onPatch={(patch) => act(() => api.updateCombatant(campaignId, combatant.id, patch))}
              onRemove={() => act(() => api.removeCombatant(campaignId, combatant.id))}
            />
          ))}
        </ol>
      )}

      <AddCombatantModal
        open={adding}
        onClose={() => setAdding(false)}
        onAdd={(input) => act(() => api.addCombatant(campaignId, input)).then(() => setAdding(false))}
      />
    </div>
  )
}

function CombatantRow({
  combatant, active, isDm, onPatch, onRemove,
}: {
  combatant: Combatant
  active: boolean
  isDm: boolean
  onPatch: (patch: Record<string, unknown>) => void
  onRemove: () => void
}) {
  const [showConditions, setShowConditions] = useState(false)
  const [delta, setDelta] = useState(0)
  const down = combatant.status === 'down'

  return (
    <li
      className={cn(
        'rounded-[--radius-card] border p-3 transition-colors',
        active ? 'border-gold-500 bg-gold-900/25 ring-1 ring-gold-500/30'
        : down ? 'border-ink-700/60 bg-ink-900/40 opacity-60'
        : 'border-ink-700/70 bg-ink-850/70',
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className={cn(
          'grid size-10 shrink-0 place-items-center rounded-xl border font-mono text-lg tabular-nums',
          active ? 'border-gold-500 bg-gold-500 text-on-accent' : 'border-ink-600 bg-ink-800 text-ink-200',
        )}>
          {combatant.initiative}
        </span>

        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 truncate text-sm">
            <span className={active ? 'text-gold-400' : 'text-ink-100'}>{combatant.name}</span>
            {combatant.isEnemy && <Badge tone="blood">enemy</Badge>}
            {active && <Badge tone="gold">up now</Badge>}
          </p>

          {/* Players get a word for enemy health; the DM gets the numbers. */}
          <p className="mt-0.5 text-xs">
            {combatant.hpCurrent === null ? (
              <span className={STATUS_TONE[combatant.status]}>{STATUS_LABEL[combatant.status]}</span>
            ) : (
              <span className="font-mono text-ink-400">
                {combatant.hpCurrent}<span className="text-ink-600">/{combatant.hpMax}</span>
                {combatant.armorClass !== null && <span className="ml-2 text-ink-500">AC {combatant.armorClass}</span>}
              </span>
            )}
          </p>
        </div>

        {isDm && (
          <div className="flex shrink-0 items-center gap-1.5">
            <NumberField
              min={0} max={999} fallback={0} value={delta} onChange={setDelta}
              aria-label={`Amount for ${combatant.name}`} className="h-8 w-16"
            />
            <Button size="sm" variant="danger" disabled={!delta}
              onClick={() => { onPatch({ hpCurrent: (combatant.hpCurrent ?? 0) - delta }); setDelta(0) }}>
              −
            </Button>
            <Button size="sm" disabled={!delta}
              className="border-moss-500/40 text-moss-500 hover:bg-moss-900"
              onClick={() => {
                const next = Math.min(combatant.hpMax ?? 999, (combatant.hpCurrent ?? 0) + delta)
                onPatch({ hpCurrent: next }); setDelta(0)
              }}>
              +
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowConditions((v) => !v)}
              aria-expanded={showConditions}>
              Cond.
            </Button>
            <Button size="sm" variant="ghost" onClick={onRemove} aria-label={`Remove ${combatant.name}`}>✕</Button>
          </div>
        )}
      </div>

      {combatant.conditions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {combatant.conditions.map((condition) => (
            <Badge key={condition} tone="arcane">{condition}</Badge>
          ))}
        </div>
      )}

      {isDm && showConditions && (
        <div className="mt-3 flex flex-wrap gap-1.5 border-t border-ink-700/60 pt-3">
          {CONDITIONS.map((condition) => {
            const on = combatant.conditions.includes(condition)
            return (
              <button
                key={condition}
                type="button"
                aria-pressed={on}
                onClick={() => onPatch({
                  conditions: on
                    ? combatant.conditions.filter((c) => c !== condition)
                    : [...combatant.conditions, condition],
                })}
                className={cn(
                  'rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                  on ? 'border-arcane-500 bg-arcane-900/60 text-arcane-400'
                     : 'border-ink-700 text-ink-500 hover:border-ink-500',
                )}
              >
                {condition}
              </button>
            )
          })}
        </div>
      )}
    </li>
  )
}

function StartModal({
  open, onClose, onStart,
}: {
  open: boolean
  onClose: () => void
  onStart: (input: { name: string; includeParty: boolean }) => void
}) {
  const [name, setName] = useState('Combat')
  const [includeParty, setIncludeParty] = useState(true)

  return (
    <Modal open={open} title="Start combat" onClose={onClose}>
      <div className="space-y-4">
        <Field label="What is this fight?">
          {(id) => <Input id={id} value={name} maxLength={80}
            onChange={(e) => setName(e.target.value)} placeholder="Ambush at the hedge" />}
        </Field>
        <label className="flex items-center gap-2 text-sm text-ink-300">
          <input type="checkbox" checked={includeParty} onChange={(e) => setIncludeParty(e.target.checked)}
            className="size-4 accent-gold-500" />
          Add the whole party automatically
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => onStart({ name: name.trim() || 'Combat', includeParty })}>
            Start
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function AddCombatantModal({
  open, onClose, onAdd,
}: {
  open: boolean
  onClose: () => void
  onAdd: (input: Record<string, unknown>) => void
}) {
  const [name, setName] = useState('')
  const [hpMax, setHpMax] = useState(10)
  const [armorClass, setArmorClass] = useState(12)
  const [initiative, setInitiative] = useState(0)
  const [hpHidden, setHpHidden] = useState(true)

  function submit(event: FormEvent) {
    event.preventDefault()
    onAdd({ name: name.trim(), hpMax, hpCurrent: hpMax, armorClass, initiative, isEnemy: true, hpHidden })
    setName('')
  }

  return (
    <Modal open={open} title="Add a creature" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          {(id) => <Input id={id} value={name} maxLength={80} autoFocus
            onChange={(e) => setName(e.target.value)} placeholder="Redcap" />}
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Hit points">
            {(id) => <NumberField id={id} min={0} max={9999} fallback={10} value={hpMax}
              onChange={setHpMax} className="w-full" />}
          </Field>
          <Field label="AC">
            {(id) => <NumberField id={id} min={0} max={40} fallback={12} value={armorClass}
              onChange={setArmorClass} className="w-full" />}
          </Field>
          <Field label="Initiative" hint="Leave 0 to roll.">
            {(id) => <NumberField id={id} min={-20} max={60} fallback={0} value={initiative}
              onChange={setInitiative} className="w-full" />}
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-ink-300">
          <input type="checkbox" checked={hpHidden} onChange={(e) => setHpHidden(e.target.checked)}
            className="size-4 accent-gold-500" />
          Hide its hit points from players
        </label>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={!name.trim()}>Add</Button>
        </div>
      </form>
    </Modal>
  )
}
